import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { ActivatedRoute, Params, RouterLink } from '@angular/router';
import { TAdPlacement } from '@x/common/ads/ad.config';
import { AdService } from '@x/common/ads/ad.service';
import { AdPictureComponent } from '@x/common/ads/components/ad-picture/ad-picture.component';
import { AdVideoComponent } from '@x/common/ads/components/ad-video/ad-video.component';
import { WindowRef } from '@x/common/browser';
import { TShopAd } from '@x/ecommerce/shop-client';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';

type TLinkState = {
  queryParams: Params;
  relativeUrl?: string | null;
  absoluteUrl?: string | null;
  newTab: boolean;
};

@Component({
  selector: 'x-ad',
  standalone: true,
  imports: [AsyncPipe, AdPictureComponent, AdVideoComponent, RouterLink, NgTemplateOutlet],
  templateUrl: './ad.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'x-ad' },
})
export class AdComponent implements OnInit, OnDestroy {
  private adToRelease: TShopAd | null;

  @Input({ required: true }) placement: TAdPlacement;

  @HostBinding('class')
  get hostClass() {
    return `${this.placement.toLowerCase()}`;
  }

  @Input() ad: TShopAd;
  @Input() taxonPath: string | undefined;
  @Input() taxonId?: number;

  ad$: Observable<TShopAd | null | undefined> = of(null);
  adLinkState$ = new BehaviorSubject<TLinkState | null>(null);

  constructor(
    private route: ActivatedRoute,
    private wr: WindowRef,
    @Optional() private shopAdsService?: AdService,
  ) {}

  ngOnDestroy() {
    this.releaseAd();
  }

  releaseAd() {
    if (!this.shopAdsService || !this.adToRelease) return;
    this.shopAdsService.releaseAd(this.adToRelease);
  }

  ngOnInit(): void {
    if (!this.shopAdsService) return;

    this.ad$ = of(this.ad).pipe(
      switchMap((ad) => {
        if (ad) return of(ad);

        return this.shopAdsService!.ad$(this.placement, this.taxonPath);
      }),
      tap((ad) => {
        if (ad) {
          this.adToRelease = ad;
          this.adLinkState$.next(this.getLinkState(ad));
        }
      }),
    );
  }

  getMediaType(ad: TShopAd): 'image' | 'video' {
    return ad.media[0].type === 'image' ? 'image' : 'video';
  }

  getLinkState(ad: TShopAd): TLinkState {
    const baseQueryParams = { shopAdId: ad.id, shopAdPlacement: this.placement };
    const newTab = ad.urlTarget === '_blank';

    if (this.isAbsoluteUrl(ad.url)) {
      return this.parseAbsoluteUrl(ad.url, baseQueryParams, newTab);
    }
    
    return this.parseRelativeUrl(ad.url, baseQueryParams, newTab);
  }

  private isAbsoluteUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  private parseAbsoluteUrl(url: string, baseQueryParams: Params, newTab: boolean): TLinkState {
    const absoluteUrl = new URL(url);
    return {
      queryParams: baseQueryParams,
      absoluteUrl: absoluteUrl.href + this.formatQueryParams(absoluteUrl.search),
      newTab,
    };
  }

  private parseRelativeUrl(url: string, baseQueryParams: Params, newTab: boolean): TLinkState {
    const relativeUrl = this.validRelativeUrl(url);
    
    if (!relativeUrl) {
      return { queryParams: baseQueryParams, newTab };
    }

    if (newTab) {
      return {
        queryParams: baseQueryParams,
        absoluteUrl: new URL(this.wr.instance.location.origin + relativeUrl).href,
        newTab,
      };
    }

    const [path, queryString] = relativeUrl.split('?');
    const mergedQueryParams = this.buildQueryParams(baseQueryParams, queryString);

    return {
      queryParams: mergedQueryParams,
      relativeUrl: path,
      newTab,
    };
  }

  private buildQueryParams(baseParams: Params, queryString?: string): Params {
    const mergedParams = { ...baseParams };
    
    if (queryString) {
      const urlParams = new URLSearchParams(queryString);
      urlParams.forEach((value, key) => {
        mergedParams[key] = value;
      });
    }
    
    return mergedParams;
  }

  private formatQueryParams(existingParams: string): string {
    const urlParams = new URLSearchParams(existingParams);
    urlParams.set('shopAdId', this.ad.id.toString());
    urlParams.set('shopAdPlacement', this.placement.toString());
    return `?${urlParams.toString()}`;
  }

  private validRelativeUrl(url: string): string | null {
    const channelCode: string = this.route.snapshot.params.channelCode;

    if (!channelCode) return null;

    // Ensure the URL starts with a '/'
    if (!url.startsWith('/')) url = '/' + url;

    // add channel if not present
    if (!url.startsWith(`/${channelCode}`)) {
      url = `/${channelCode}${url}`;
    }

    // Split URL into path and query parts
    const [path] = url.split('?');

    const relativeUrlPattern = /^\/[a-zA-Z0-9-._~%!$&'()*+,;=:@/]*$/;

    const relativeUrl = relativeUrlPattern.test(path);

    return relativeUrl ? url : null;
  }
}
